/**
 * Oatmeal Loader — global singleton
 * - Mounts once from #oat-loader
 * - Thresholded show (prevents flicker)
 * - Reference counted (safe with nested async)
 *
 * API:
 *   window.OatLoader.start({ delay?: 150 })
 *   window.OatLoader.stop()
 *   window.OatLoader.wrap(promiseOrFn, { delay?: 150 })
 *   window.OatLoader.show() / hide()  (low-level)
 */

(function () {
  'use strict';

  const el = document.getElementById('oat-loader');
  if (!el) return;
  const SKIP_BEFOREUNLOAD_KEY = 'oat-skip-next-beforeunload-loader';
  const CARRY_OVER_KEY = 'oat-loader-carry-over';
  const CONTEXT_KEY = 'oat-loader-context';

  let timer = null;
  let shown = el.classList.contains('is-visible') || el.getAttribute('aria-hidden') === 'false';
  let depth = 0;

  function isVariantCollectionPending() {
    return document.body?.hasAttribute('data-oat-variant-collection-loading');
  }

  function setContext(context, persist = true) {
    if (context) {
      document.documentElement?.setAttribute('data-oat-loader-context', context);
      if (persist) {
        try { sessionStorage.setItem(CONTEXT_KEY, context); } catch (_) {}
      }
      return;
    }

    document.documentElement?.removeAttribute('data-oat-loader-context');
    if (persist) {
      try { sessionStorage.removeItem(CONTEXT_KEY); } catch (_) {}
    }
  }

  function clearCarryOver() {
    try { sessionStorage.removeItem(CARRY_OVER_KEY); } catch (_) {}
    document.documentElement?.removeAttribute('data-oat-loader-carry');
  }

  function show() {
    if (shown) return;
    el.classList.add('is-visible');
    el.setAttribute('aria-hidden', 'false');
    shown = true;
  }

  function hide() {
    if (!shown) return;
    el.classList.remove('is-visible');
    el.setAttribute('aria-hidden', 'true');
    shown = false;
    if (!isVariantCollectionPending()) {
      setContext(null);
    }
  }

  function start(opts) {
    const delay = Number(opts?.delay ?? 150);

    depth += 1;

    // already visible or already scheduled -> keep it
    if (shown || timer) return;

    timer = setTimeout(() => {
      timer = null;
      // show only if still in-flight
      if (depth > 0) show();
    }, delay);
  }

  function stop() {
    depth = Math.max(0, depth - 1);

    // if still in-flight, don't hide
    if (depth > 0) return;

    // cancel pending show
    if (timer) {
      clearTimeout(timer);
      timer = null;
    }

    hide();
  }

  function wrap(promiseOrFn, opts) {
    start(opts);
    try {
      const p = (typeof promiseOrFn === 'function') ? promiseOrFn() : promiseOrFn;
      return Promise.resolve(p).finally(stop);
    } catch (err) {
      stop();
      throw err;
    }
  }

  window.OatLoader = { start, stop, wrap, show, hide, setContext };

  // Optional: show on navigation, hide on restore
  window.addEventListener('beforeunload', () => {
    try {
      if (sessionStorage.getItem(SKIP_BEFOREUNLOAD_KEY) === '1') {
        sessionStorage.removeItem(SKIP_BEFOREUNLOAD_KEY);
        return;
      }
    } catch (_) {}

    start({ delay: 0 });
  });
  window.addEventListener('pageshow', () => {
    // Reset state on bfcache restores
    depth = 0;
    if (timer) { clearTimeout(timer); timer = null; }
    try { sessionStorage.removeItem(SKIP_BEFOREUNLOAD_KEY); } catch (_) {}
    if (!isVariantCollectionPending()) clearCarryOver();
    if (isVariantCollectionPending()) return;
    hide();
  });
  window.addEventListener('load', () => {
    depth = 0;
    if (timer) { clearTimeout(timer); timer = null; }
    try { sessionStorage.removeItem(SKIP_BEFOREUNLOAD_KEY); } catch (_) {}
    if (!isVariantCollectionPending()) clearCarryOver();
    if (isVariantCollectionPending()) return;
    hide();
  });

  // Optional: internal link clicks
  document.addEventListener('click', function (e) {
    const a = e.target.closest && e.target.closest('a[href]');
    if (!a) return;

    if (
      a.hasAttribute('data-oat-size-chart') ||
      a.classList.contains('oat-qa-size-chart') ||
      a.classList.contains('variant-option__size-chart')
    ) {
      return;
    }

    if (a.target === '_blank') return;
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;

    const href = a.getAttribute('href') || '';
    if (!href || href.startsWith('#') || a.hasAttribute('download')) return;

    try {
      const u = new URL(a.href, window.location.href);
      if (u.origin !== window.location.origin) return;
      if (u.pathname.includes('/products/')) {
        setContext('product');
      } else if (u.pathname.startsWith('/collections/')) {
        setContext('collection');
        try { sessionStorage.setItem(SKIP_BEFOREUNLOAD_KEY, '1'); } catch (_) {}
        try { sessionStorage.setItem(CARRY_OVER_KEY, 'collection-nav'); } catch (_) {}
        document.documentElement?.setAttribute('data-oat-loader-carry', '1');
        start({ delay: 0 });
        return;
      } else {
        setContext(null);
      }
    } catch (_) {
      return;
    }

    start({ delay: 0 });
  }, { capture: true });

})();
